import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { flip, FloatingPortal, offset, shift, useClick, useFloating, useInteractions } from '@floating-ui/react';
import { Menu, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';

import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useCurrentUser, useSignOut, useSwitchPartner } from 'services/repositories/user/user';
import { cn } from 'support/helpers/cn/cn';
import { AuthRoutes, DashboardRoutes } from 'support/types';
import { PartnerLogo } from 'components/PartnerLogo/PartnerLogo';

export const WorkspaceMenu = ({ expanded }: { expanded: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const { data: currentPartner } = useCurrentPartner();
  const { mutate: logout } = useSignOut();
  const { mutate: switchPartner } = useSwitchPartner();

  const { refs, floatingStyles, context } = useFloating({
    placement: 'right-start',
    strategy: 'fixed',
    middleware: [offset(8), flip(), shift()],
  });
  const click = useClick(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click]);

  if (!currentUser || !currentPartner) {
    return null;
  }

  const partners = currentUser.partners ?? [];
  const hasMultiplePartners = partners.length > 1;

  const handleSwitchPartner = (partnerId: string) => {
    if (partnerId === currentPartner.id) {
      return;
    }
    switchPartner(
      { partner_id: partnerId },
      {
        onSuccess: () => {
          navigate(DashboardRoutes.root, { replace: true });
        },
      },
    );
  };

  const handleLogout = () => {
    logout(null, {
      onSuccess: () => {
        navigate(AuthRoutes.login, { replace: true });
      },
    });
  };

  return (
    <Menu as="div" className="shrink-0">
      {({ open }) => (
        <>
          <Menu.Button
            ref={refs.setReference}
            className="flex w-full items-center gap-3 px-4 pt-4 pb-3 text-left hover:bg-gray-200 focus-visible:outline-hidden"
            {...getReferenceProps()}
          >
            <PartnerLogo imageUrl={currentPartner.logoUrl} name={currentPartner.name} size="base" />
            {expanded && (
              <>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="truncate text-sm leading-5 font-semibold text-gray-700">{currentPartner.name}</span>
                  <span className="truncate text-xs leading-4 font-medium text-gray-600">{currentUser.name}</span>
                </div>
                <ChevronDownIcon className="size-5 shrink-0 text-gray-500" />
              </>
            )}
          </Menu.Button>

          <FloatingPortal>
            <Transition
              show={open}
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                static
                ref={refs.setFloating}
                style={floatingStyles}
                className="z-500 w-78 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus-visible:outline-hidden"
                {...getFloatingProps()}
              >
                {hasMultiplePartners && (
                  <>
                    <div className="px-3 py-2">
                      <span className="text-xs font-medium text-gray-500">{t('common:workspaceMenu.workspaces')}</span>
                    </div>
                    <div className="max-h-[min(60vh,400px)] overflow-y-auto">
                      {partners.map((partner) => {
                        const isActive = partner.id === currentPartner.id;
                        return (
                          <Menu.Item key={partner.id}>
                            {({ active }) => (
                              <button
                                className={cn('flex w-full items-center gap-3 px-3 py-2', active ? 'bg-gray-100' : '')}
                                onClick={() => handleSwitchPartner(partner.id)}
                              >
                                <PartnerLogo imageUrl={partner.logo_url} name={partner.name} size="small" />
                                <span
                                  className={cn(
                                    'flex-1 truncate text-left text-sm text-gray-900',
                                    isActive ? 'font-semibold' : '',
                                  )}
                                >
                                  {partner.name}
                                </span>
                                {isActive && <CheckIcon className="size-5 shrink-0 text-emerald-500" />}
                              </button>
                            )}
                          </Menu.Item>
                        );
                      })}
                    </div>
                    <div className="my-1 border-t border-gray-200" />
                  </>
                )}
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={cn(
                        'flex w-full items-center px-3.5 py-2 text-sm text-gray-700',
                        active ? 'bg-gray-100' : '',
                      )}
                      onClick={handleLogout}
                    >
                      {t('auth:logout')}
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </FloatingPortal>
        </>
      )}
    </Menu>
  );
};
